import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, X, Bed, Bath, Square, MapPin, Calendar, Home, Car, Check, Minus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useCompare } from '../context/CompareContext';

const ComparePage = () => {
  const { compareList, removeFromCompare, clearCompare } = useCompare();

  if (compareList.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">No Properties to Compare</h1>
          <p className="text-gray-600 mb-6">Add properties to your compare list from the search page.</p>
          <Link to="/search">
            <Button className="bg-red-600 hover:bg-red-700">
              <ArrowLeft size={18} className="mr-2" />
              Back to Search
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // Helper to render comparison row
  const CompareRow = ({ label, icon: Icon, getValue, formatValue, highlight = false }) => {
    const values = compareList.map(p => getValue(p));
    const maxValue = Math.max(...values.filter(v => typeof v === 'number'));
    
    return (
      <tr className={highlight ? 'bg-red-50' : ''}>
        <td className="py-3 px-4 font-medium text-gray-700 border-r flex items-center gap-2">
          {Icon && <Icon size={16} className="text-gray-400" />}
          {label}
        </td>
        {compareList.map((property, idx) => {
          const value = getValue(property);
          const isMax = highlight && typeof value === 'number' && value === maxValue;
          return (
            <td 
              key={idx} 
              className={`py-3 px-4 text-center border-r last:border-r-0 ${isMax ? 'text-red-600 font-bold' : ''}`}
            >
              {formatValue ? formatValue(value, property) : (value || '-')}
            </td>
          );
        })}
      </tr>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/search">
                <Button variant="outline" size="sm">
                  <ArrowLeft size={18} className="mr-2" />
                  Back to Search
                </Button>
              </Link>
              <h1 className="text-xl font-bold text-gray-900">
                Compare Properties ({compareList.length})
              </h1>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={clearCompare}
              className="text-red-600 border-red-200 hover:bg-red-50"
            >
              Clear All
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Property Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {compareList.map((property) => (
            <Card key={property.id} className="relative group">
              <button
                onClick={() => removeFromCompare(property.id)}
                className="absolute top-2 right-2 z-10 bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X size={16} />
              </button>
              <div className="h-40 overflow-hidden rounded-t-lg">
                <img 
                  src={property.image} 
                  alt={property.address}
                  className="w-full h-full object-cover"
                />
              </div>
              <CardContent className="p-3">
                <h3 className="font-semibold text-gray-900 text-sm truncate">{property.address}</h3>
                <p className="text-gray-500 text-xs truncate">{property.city}, {property.province}</p>
                <p className="text-red-600 font-bold mt-1">${property.price.toLocaleString()}</p>
              </CardContent>
            </Card>
          ))}
          
          {/* Add more placeholder */}
          {compareList.length < 4 && (
            <Link to="/search" className="block">
              <Card className="h-full min-h-[200px] border-2 border-dashed border-gray-300 hover:border-red-400 transition-colors flex items-center justify-center">
                <div className="text-center text-gray-400">
                  <span className="text-3xl">+</span>
                  <p className="text-sm mt-1">Add Property</p>
                </div>
              </Card>
            </Link>
          )}
        </div>

        {/* Comparison Table */}
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-100 border-b">
                  <th className="py-3 px-4 text-left font-semibold text-gray-700 w-48 border-r">Feature</th>
                  {compareList.map((property, idx) => (
                    <th key={idx} className="py-3 px-4 text-center font-semibold text-gray-700 border-r last:border-r-0">
                      {property.address.split(' ').slice(0, 2).join(' ')}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <CompareRow 
                  label="Price" 
                  getValue={p => p.price}
                  formatValue={v => <span className="font-bold text-red-600">${v?.toLocaleString()}</span>}
                />
                <CompareRow 
                  label="Bedrooms" 
                  icon={Bed}
                  getValue={p => p.beds}
                  formatValue={v => `${v} Beds`}
                  highlight
                />
                <CompareRow 
                  label="Bathrooms" 
                  icon={Bath}
                  getValue={p => p.baths}
                  formatValue={v => `${v} Baths`}
                  highlight
                />
                <CompareRow 
                  label="Square Feet" 
                  icon={Square}
                  getValue={p => p.sqft}
                  formatValue={v => v > 0 ? `${v?.toLocaleString()} sqft` : 'N/A'}
                  highlight
                />
                <CompareRow 
                  label="Property Type" 
                  icon={Home}
                  getValue={p => p.propertyType}
                />
                <CompareRow 
                  label="City" 
                  icon={MapPin}
                  getValue={p => p.city}
                />
                <CompareRow 
                  label="Postal Code" 
                  getValue={p => p.postalCode}
                />
                <CompareRow 
                  label="Year Built" 
                  icon={Calendar}
                  getValue={p => p.yearBuilt}
                  formatValue={v => v || 'N/A'}
                />
                <CompareRow 
                  label="Garage" 
                  icon={Car}
                  getValue={p => p.garageSpaces}
                  formatValue={v => v ? `${v} Car` : 'N/A'}
                />
                <CompareRow 
                  label="MLS#" 
                  getValue={p => p.mlsNumber}
                />
                <CompareRow 
                  label="Status" 
                  getValue={p => p.status}
                  formatValue={v => (
                    <Badge className={v === 'Active' ? 'bg-green-600' : 'bg-gray-600'}>
                      {v}
                    </Badge>
                  )}
                />
                <CompareRow 
                  label="Price/sqft" 
                  getValue={p => p.sqft > 0 ? Math.round(p.price / p.sqft) : null}
                  formatValue={v => v ? `$${v}/sqft` : 'N/A'}
                />
              </tbody>
            </table>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex justify-center gap-4 mt-8">
          <Link to="/search">
            <Button variant="outline">
              <ArrowLeft size={18} className="mr-2" />
              Continue Shopping
            </Button>
          </Link>
          <Link to="/contact">
            <Button className="bg-red-600 hover:bg-red-700">
              Contact About These Properties
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ComparePage;
